import { useEffect, useState } from 'react'
import { Description, Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { Text } from '../../catalyst/text'
import { dropNulls } from '../../lib/api'
import { formatArea } from '../../lib/area'
import { massUnitFor, otherMassLabel } from '../../lib/mass'
import { checkHarvest, describeBlocking } from '../../lib/canadagap'
import type { HarvestLog } from '../../lib/types'
import { answerLabel, ChoiceButtons, SameAsLast, YesNo } from '../chips'
import { amountProblem, fieldIdsOf, fieldValues, forField, optNum, optText, seasonYearOf, str, type FlowDef, type StepCtx } from '../flow'
import type { Values } from '../use-draft'
import { AmountInput, lastFieldLog, placeOf } from './shared'

type PhiCheck = { state: 'loading' | 'error' } | { state: 'clear' } | { state: 'blocking'; lines: string[] }

/** Each chosen field with its own values on top of the shared ones (one, with none chosen yet). */
const eachField = (v: Values): Values[] => {
  const ids = fieldIdsOf(v)
  return ids.length === 0 ? [v] : ids.map((id) => ({ ...v, ...fieldValues(v, id) }))
}

/**
 * One field's pre-harvest interval, checked for the grower (every application on this field whose
 * waiting period covers the date): when nothing blocks, "Yes" is answered for them, still
 * changeable; when something does, it is listed and the answer is left to them.
 */
function FieldPhiCheck({ ctx, showName }: { ctx: StepCtx; showName: boolean }) {
  const { orgId, farmId, values, patch, field } = ctx
  const fieldId = str(values.fieldId)
  const date = str(values.date)
  const [check, setCheck] = useState<PhiCheck>({ state: 'loading' })

  useEffect(() => {
    if (!fieldId || !date) return
    let cancelled = false
    setCheck({ state: 'loading' })
    checkHarvest(orgId, farmId, fieldId, date).then(
      ({ blocking }) => {
        if (cancelled) return
        setCheck(blocking.length === 0 ? { state: 'clear' } : { state: 'blocking', lines: blocking.map(describeBlocking) })
      },
      () => {
        if (!cancelled) setCheck({ state: 'error' })
      },
    )
    return () => {
      cancelled = true
    }
  }, [orgId, farmId, fieldId, date])

  // Answer "Yes" only for a clear check the grower hasn't answered; take it back if the check stops
  // being clear (the date or field changed), so a "Yes" we gave never outlives the facts behind it.
  useEffect(() => {
    if (check.state === 'clear' && values.phiVerified === undefined) patch({ phiVerified: true, phiAuto: true })
    if (check.state === 'blocking' && values.phiAuto === true) patch({ phiVerified: undefined, phiAuto: false })
  }, [check.state, values.phiVerified, values.phiAuto, patch])

  const name = field?.name ?? 'this field'
  return (
    <div className={showName ? 'space-y-3 rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10' : undefined}>
      {showName && <p className="font-semibold text-zinc-950 dark:text-white">{name}</p>}
      <YesNo
        label={
          showName
            ? `Pre-harvest interval checked on ${name}? Nothing sprayed or applied is still inside its waiting period.`
            : 'Did you check the pre-harvest interval? Nothing sprayed or applied is still inside its waiting period.'
        }
        value={values.phiVerified}
        onChange={(a) => patch({ phiVerified: a, phiAuto: false })}
      />
      {check.state === 'loading' ? (
        <Text className="mt-2">Checking {name}’s waiting periods…</Text>
      ) : check.state === 'clear' ? (
        <p className="mt-2 rounded-lg bg-emerald-50 p-3 text-sm/6 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          No waiting periods active on {name} for {date}.
        </p>
      ) : check.state === 'blocking' ? (
        <div className="mt-2 space-y-1 rounded-lg bg-amber-50 p-3 text-sm/6 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
          {check.lines.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** The two attestations, asked before anything else: the hazard walk once, the waiting periods per field. */
function ChecksStep(ctx: StepCtx) {
  const { values, patch, selectedFields } = ctx
  return (
    <FieldGroup>
      <div>
        <YesNo label="Was the site assessed for hazards before harvest?" value={values.siteAssessed} onChange={(a) => patch({ siteAssessed: a })} />
        {values.siteAssessed === false && (
          <p className="mt-2 rounded-lg bg-amber-50 p-3 text-sm/6 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
            Before picking, walk the field for animal intrusion or droppings, flooding, nearby manure or compost, spray drift, and broken glass or
            other debris. You can still record this harvest.
          </p>
        )}
      </div>
      {selectedFields.map((f) => (
        <FieldPhiCheck key={f.id} ctx={forField(ctx, f)} showName={selectedFields.length > 1} />
      ))}
    </FieldGroup>
  )
}

const OTHER = '__other'

/** What came off one field: the variety (taken from what is planted when that's one variety) and how much. */
function FieldYield({ ctx, unit, showName }: { ctx: StepCtx; unit: string; showName: boolean }) {
  const { values, patch, field, areaUnit } = ctx
  const planted = [...new Set(field?.plantings?.map((p) => p.variety).filter((v): v is string => !!v) ?? [])]
  const variety = str(values.variety)
  const [typing, setTyping] = useState(false)

  // One variety in the field: that's what came off, unless the grower says otherwise.
  const only = planted.length === 1 ? planted[0]! : null
  useEffect(() => {
    if (only && values.variety === undefined) patch({ variety: only })
  }, [only, values.variety, patch])

  const input = (
    <FormField>
      <Label>Variety</Label>
      <Input value={variety} onChange={(e) => patch({ variety: e.target.value })} placeholder="e.g. Duke" autoComplete="off" />
    </FormField>
  )
  const other = otherMassLabel(optNum(values.quantity), unit)

  return (
    <div className={showName ? 'space-y-4 rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10' : 'space-y-8'}>
      {showName && field && (
        <p className="font-semibold text-zinc-950 dark:text-white">
          {field.name} <span className="font-normal text-zinc-500 dark:text-zinc-400">· {formatArea(field.areaM2, areaUnit)}</span>
        </p>
      )}
      {only && !typing && variety === only ? (
        <div className="flex items-center justify-between gap-3 text-sm/6">
          <span className="text-zinc-950 dark:text-white">
            <span className="font-medium">{only}</span> <span className="text-zinc-500 dark:text-zinc-400">· planted here</span>
          </span>
          <button type="button" className="min-h-11 font-medium text-emerald-700 dark:text-emerald-400" onClick={() => setTyping(true)}>
            Change
          </button>
        </div>
      ) : planted.length > 1 ? (
        <>
          <ChoiceButtons
            label="Variety"
            value={planted.includes(variety) && !typing ? variety : typing ? OTHER : ''}
            options={[...planted.map((v) => ({ value: v, label: v })), { value: OTHER, label: 'Other' }]}
            onChange={(v) => {
              setTyping(v === OTHER)
              patch({ variety: v === OTHER ? '' : v })
            }}
          />
          {typing && input}
        </>
      ) : (
        input
      )}
      <FormField>
        <Label>How much? ({unit || 'amount'})</Label>
        <AmountInput value={values.quantity} onChange={(q) => patch({ quantity: q })} />
        {other && <Description>{other}</Description>}
      </FormField>
    </div>
  )
}

/** One row per field (variety and amount), then the unit, packaging and storage shared by all. */
function WhatStep(ctx: StepCtx) {
  const { values, patch, last, selectedFields } = ctx
  const l = last as HarvestLog | null
  const unit = str(values.quantityUnit)
  const fieldList = selectedFields.length ? selectedFields : ctx.field ? [ctx.field] : []
  // Weighed in the org's unit (lb for acres, kg for hectares) unless the grower or last time says otherwise.
  useEffect(() => {
    if (values.quantityUnit === undefined) patch({ quantityUnit: massUnitFor(ctx.areaUnit) })
  }, [values.quantityUnit, ctx.areaUnit, patch])
  // A unit from an older entry ("flats") stays choosable.
  const units: string[] = unit && !UNITS.includes(unit as (typeof UNITS)[number]) ? [...UNITS, unit] : [...UNITS]

  return (
    <FieldGroup>
      {l && (
        <SameAsLast
          label={[l.productVariety, l.packagingMaterials].filter(Boolean).join(', ')}
          onClick={() => patch({ variety: l.productVariety, packaging: l.packagingMaterials ?? '', quantityUnit: l.quantityUnit })}
        />
      )}
      <ChoiceButtons
        label="Weighed in"
        value={unit}
        options={units.map((u) => ({ value: u, label: u }))}
        onChange={(u) => patch({ quantityUnit: u })}
      />
      {fieldList.map((f) => (
        <FieldYield key={f.id} ctx={forField(ctx, f)} unit={unit} showName={fieldList.length > 1} />
      ))}
      <FormField>
        <Label>Packaging materials</Label>
        <Input value={str(values.packaging)} onChange={(e) => patch({ packaging: e.target.value })} autoComplete="off" />
      </FormField>
      <FormField>
        <Label>Stored on (optional)</Label>
        <Input type="date" value={str(values.storedOn)} onChange={(e) => patch({ storedOn: e.target.value })} />
      </FormField>
    </FieldGroup>
  )
}

const UNITS = ['lb', 'kg'] as const

const total = (v: Values) => eachField(v).reduce((sum, f) => sum + (optNum(f.quantity) ?? 0), 0)

export const harvestFlow: FlowDef = {
  kind: 'harvest',
  checkForm: 'p2',
  label: 'Harvested',
  needsField: true,
  // The two attestations start unanswered: the grower says yes or no to each (see YesNo).
  severalFields: true,
  // Per field: the waiting-period answer, variety and amount (values.perField). Shared: the rest.
  initial: ({ today, fieldId }) => ({ fieldId, date: today }),
  fetchLast: (orgId, farmId, fieldId) => lastFieldLog<HarvestLog>('harvest-logs', orgId, farmId, fieldId),
  steps: [
    {
      id: 'checks',
      title: 'Before you pick',
      // Both are required columns on Form P2, so each needs an answer, for every field.
      valid: (v) => typeof v.siteAssessed === 'boolean' && eachField(v).every((f) => typeof f.phiVerified === 'boolean'),
      render: (ctx) => <ChecksStep {...ctx} />,
    },
    {
      id: 'what',
      title: 'What did you harvest?',
      valid: (v) =>
        str(v.quantityUnit).trim() !== '' &&
        eachField(v).every((f) => str(f.variety).trim() !== '' && Number(str(f.quantity)) > 0 && !amountProblem(f.quantity)),
      render: (ctx) => <WhatStep {...ctx} />,
    },
  ],
  review: (ctx) => {
    const { values, selectedFields } = ctx
    const unit = str(values.quantityUnit)
    const several = selectedFields.length > 1
    const perField = selectedFields.length ? selectedFields.map((f) => ({ name: f.name, v: forField(ctx, f).values })) : [{ name: '', v: values }]
    return {
      title: `Harvest${placeOf(ctx)}`,
      lines: [
        { label: 'Date', value: str(values.date) },
        ...perField.flatMap(({ name, v }) =>
          several
            ? [{ label: name, value: `${str(v.variety)}, ${str(v.quantity)} ${unit} · interval checked: ${answerLabel(v.phiVerified).toLowerCase()}` }]
            : [
                { label: 'Variety', value: str(v.variety) },
                { label: 'Amount', value: [`${str(v.quantity)} ${unit}`, otherMassLabel(optNum(v.quantity), unit)].filter(Boolean).join(' ') },
                { label: 'Interval checked', value: answerLabel(v.phiVerified) },
              ],
        ),
        ...(several ? [{ label: 'Total', value: [`${total(values)} ${unit}`, otherMassLabel(total(values), unit)].filter(Boolean).join(' ') }] : []),
        ...(optText(values.packaging) ? [{ label: 'Packaging', value: str(values.packaging) }] : []),
        ...(optText(values.storedOn) ? [{ label: 'Stored on', value: str(values.storedOn) }] : []),
        { label: 'Site assessed', value: answerLabel(values.siteAssessed) },
      ],
    }
  },
  warnings: async ({ orgId, farmId, values }) => {
    const { blocking } = await checkHarvest(orgId, farmId, str(values.fieldId), str(values.date))
    return blocking.map(describeBlocking)
  },
  entry: (ctx) => {
    const { values } = ctx
    const date = str(values.date)
    return dropNulls({
      fieldId: str(values.fieldId),
      seasonYear: seasonYearOf(date),
      productVariety: str(values.variety).trim(),
      phiVerified: values.phiVerified === true,
      siteAssessed: values.siteAssessed === true,
      harvestedOn: date,
      quantityHarvested: optNum(values.quantity) ?? 0,
      quantityUnit: str(values.quantityUnit).trim(),
      packagingMaterials: optText(values.packaging) ?? null,
      storedOn: optText(values.storedOn) ?? null,
    })
  },
  badge: { framework: 'CanadaGAP', formCode: 'P2', formName: 'Harvest, Handling and Storage' },
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/harvest-logs`,
}
